import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  DEFAULT_SHOP_LOCALE_OPTIONS,
  normalizeShopLocale,
  SHOP_STOREFRONT_LOCALE_STORAGE_KEY,
  shopStorefrontMessages,
  type ShopStorefrontLocale,
} from '@/i18n/shopStorefrontMessages'

type ShopStorefrontLocaleContextValue = {
  locale: ShopStorefrontLocale
  /** False until stored preference or shop default has been applied to `locale`. */
  localeResolved: boolean
  setLocale: (next: ShopStorefrontLocale) => void
  setSupportedLocales: (next: string[] | undefined) => void
  t: (key: string) => string
  availableLocales: Array<{ value: ShopStorefrontLocale; label: string }>
}

export const ShopStorefrontLocaleContext = createContext<ShopStorefrontLocaleContextValue | null>(null)

function labelForLocale(locale: ShopStorefrontLocale): string {
  const found = DEFAULT_SHOP_LOCALE_OPTIONS.find((o) => o.value === locale)
  return found?.label ?? locale
}

function normalizeLocaleList(raw: unknown): Array<{ value: ShopStorefrontLocale; label: string }> {
  const arr = Array.isArray(raw) ? raw : []
  const normalized = arr
    .map((v) => normalizeShopLocale(String(v ?? '').trim()))
    .filter((v, i, all) => v.length > 0 && all.indexOf(v) === i)
  const locales = normalized.length > 0 ? normalized : DEFAULT_SHOP_LOCALE_OPTIONS.map((o) => o.value)
  return locales.map((value) => ({ value, label: labelForLocale(value) }))
}

function readStoredLocale(storageKey: string): ShopStorefrontLocale | null {
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw?.trim()) return normalizeShopLocale(raw)
    // Legacy global key used before per-shop keys (`shop_storefront_locale:<slug>`).
    if (storageKey !== SHOP_STOREFRONT_LOCALE_STORAGE_KEY) {
      const legacy = localStorage.getItem(SHOP_STOREFRONT_LOCALE_STORAGE_KEY)
      if (legacy?.trim()) return normalizeShopLocale(legacy)
    }
    return null
  } catch {
    return null
  }
}

export function ShopStorefrontLocaleProvider({
  children,
  localeStorageKey = SHOP_STOREFRONT_LOCALE_STORAGE_KEY,
  /** When true, resolve with defaults immediately (template preview / embedded CMS). */
  autoResolve = false,
}: {
  children: ReactNode
  /** Per-shop key avoids one storefront's language choice affecting another. */
  localeStorageKey?: string
  autoResolve?: boolean
}) {
  const [supportedLocales, setSupportedLocalesState] = useState<string[]>(
    DEFAULT_SHOP_LOCALE_OPTIONS.map((o) => o.value)
  )
  const availableLocales = useMemo(() => normalizeLocaleList(supportedLocales), [supportedLocales])
  const defaultLocale = availableLocales[0]?.value || 'en'
  const [locale, setLocaleState] = useState<ShopStorefrontLocale>(defaultLocale)
  const [localeResolved, setLocaleResolved] = useState(false)
  const [shopLocalesConfigured, setShopLocalesConfigured] = useState(false)

  const setLocale = useCallback(
    (next: ShopStorefrontLocale) => {
      const normalized = normalizeShopLocale(next)
      const allowed = new Set(availableLocales.map((o) => o.value))
      if (!allowed.has(normalized)) return
      setLocaleState(normalized)
      try {
        localStorage.setItem(localeStorageKey, normalized)
      } catch {
        /* ignore quota / private mode */
      }
    },
    [localeStorageKey, availableLocales]
  )

  const setSupportedLocales = useCallback((next: string[] | undefined) => {
    const normalized = normalizeLocaleList(next).map((o) => o.value)
    setSupportedLocalesState(normalized)
    setShopLocalesConfigured(true)
    setLocaleResolved(false)
  }, [])

  useEffect(() => {
    if (autoResolve && !shopLocalesConfigured) {
      const stored = readStoredLocale(localeStorageKey)
      const allowed = new Set(availableLocales.map((o) => o.value))
      const target =
        stored && allowed.has(stored)
          ? stored
          : allowed.has(defaultLocale)
            ? defaultLocale
            : locale
      if (locale !== target && allowed.has(target)) {
        setLocaleState(target)
        try {
          localStorage.setItem(localeStorageKey, target)
        } catch {
          /* ignore */
        }
      }
      setLocaleResolved(true)
      return
    }
    if (!shopLocalesConfigured) return
    const allowed = new Set(availableLocales.map((o) => o.value))
    const stored = readStoredLocale(localeStorageKey)
    const target =
      stored && allowed.has(stored)
        ? stored
        : allowed.has(defaultLocale)
          ? defaultLocale
          : locale
    if (locale !== target && allowed.has(target)) {
      setLocaleState(target)
      try {
        localStorage.setItem(localeStorageKey, target)
      } catch {
        /* ignore quota / private mode */
      }
      return
    }
    setLocaleResolved(true)
  }, [
    autoResolve,
    shopLocalesConfigured,
    availableLocales,
    defaultLocale,
    locale,
    localeStorageKey,
  ])

  const t = useCallback(
    (key: string) =>
      shopStorefrontMessages[locale]?.[key] ?? shopStorefrontMessages.en[key] ?? key,
    [locale]
  )

  useEffect(() => {
    document.documentElement.lang = locale || 'en'
  }, [locale])

  const value = useMemo(
    () => ({ locale, localeResolved, setLocale, setSupportedLocales, t, availableLocales }),
    [locale, localeResolved, setLocale, setSupportedLocales, t, availableLocales]
  )

  return (
    <ShopStorefrontLocaleContext.Provider value={value}>
      {children}
    </ShopStorefrontLocaleContext.Provider>
  )
}

export function useShopStorefrontLocale(): ShopStorefrontLocaleContextValue {
  const ctx = useContext(ShopStorefrontLocaleContext)
  if (!ctx) {
    throw new Error('useShopStorefrontLocale must be used within ShopStorefrontLocaleProvider')
  }
  return ctx
}
